import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { PropTypes } from 'prop-types';
import { MdFormatListBulleted } from 'react-icons/md';
import { AppsRounded } from '../../componentsV2/Icons/Icons';
import { ToastProvider } from '../../providers';
import { withContextProvider } from '../../hoc';
import EmptyState from '../../componentsV2/EmptyState/EmptyState';
import NoSearchResults from '../../componentsV2/NoSearchResults/NoSearchResults';
import Search from '../../componentsV2/SearchBar/Search';
import tailwindTheme from '../../../../../config/tailwind.config';
import VisitsTable from '../../componentsV2/Visits/VisitsTable';
import VisitCards from '../../componentsV2/Visits/VisitCards';
import Pagination from '../../componentsV2/pagination/Pagination';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import useFetchVisits from '../../componentsV2/Visits/useFetchVisits';
import { transformCompletedStatus } from '../../utils/transformers';
import TableSkeletonLoader from '../../componentsV2/LoadingSkeletons/TableSkeletonLoader';
import VisitCardSkeletonLoader from '../../componentsV2/LoadingSkeletons/VisitCardSkeletonLoader';
import Breadcrumbs from '../../componentsV2/Breadcrumbs/Breadcrumbs';
import PagesHeader from '../../componentsV2/Headers/PagesHeader';
import { updateSearchParamsAndUrl } from '../../utils/updateSearchParamsAndUrl';
import useSyncedSearchParams from '../../hooks/useSyncedSearchParams';
import { parseParams } from '../../utils/parseParams';
import ResultCount from '../../componentsV2/ResultCount/ResultCount';
import ViewModeTooltip from '../../componentsV2/ViewModeTooltip/ViewModeTooltip';

const parseVisitParams = (searchParams) => parseParams(searchParams, {
  keyword: {},
  from: {},
  to: {},
  purpose: {},
  total_workers: {},
  workers_interacted: {},
  completed: {
    coerce: (value) => (value === null || value === 'undefined' ? '' : value),
  },
  site_name: {},
  leader: {},
  sortBy: {},
  direction: { default: 'desc' },
  perPage: { type: 'int' },
  product_id: { type: 'int' },
});

const getVisitPage = (searchParams) => parseInt(searchParams.get('page'), 10) || 1;

const VisitsPage = ({ organizationId }) => {
  const [viewType, setViewType] = useUrlSearchParams('mode', 'table');
  const [isLoading, setIsLoading] = useState(false);
  const [totalUnfilteredVisits, setTotalUnfilteredVisits] = useState(0);

  const {
    params, setParams, currentPage, setCurrentPage,
  } = useSyncedSearchParams(parseVisitParams, getVisitPage);

  const [totalVisits, setTotalVisits] = useState(0);
  // Backend /api/v1/visits endpoint currently ignores the limit param and always returns 30 records per page.
  // To keep Pagination and ResultCount accurate we match that behavior here.
  // Once backend pagination is updated we can restore view specific page sizes, for example:
  // const itemsPerPage = viewType === 'table' ? 20 : 9;
  //
  // const itemsPerPage = 30;
  const defaultItemsPerPage = viewType === 'table' ? 20 : 24;
  const itemsPerPage = params.perPage || defaultItemsPerPage;
  const currentOffset = (currentPage - 1) * itemsPerPage;
  const visits = useFetchVisits(organizationId, params, currentPage, itemsPerPage, setTotalVisits, setTotalUnfilteredVisits, setIsLoading);
  const breadcrumbLinks = [
    { href: '/visits', name: 'Visits' },
  ];

  const statusLabel = ({ true: 'Completed', false: 'Attempted' })[params.completed] || '';

  const mappedActiveSearchParams = {
    keyword: params.keyword || '',
    dateFrom: params.from || '',
    dateTo: params.to || '',
    purpose: params.purpose || '',
    workersPresent: params.total_workers || '',
    workersReached: params.workers_interacted || '',
    completed: statusLabel,
  };

  const searchFields = [
    { label: 'Keyword', name: 'keyword', type: 'text' },
    { label: 'Date From', name: 'dateFrom', type: 'date' },
    { label: 'Date To', name: 'dateTo', type: 'date' },
    { label: 'Purpose', name: 'purpose', type: 'text' },
    { label: 'Workers Present', name: 'workersPresent', type: 'number' },
    { label: 'Workers Reached', name: 'workersReached', type: 'number' },
    {
      label: 'Status', name: 'completed', type: 'dropdown', options: ['Completed', 'Attempted'], shouldShowClearOption: true, clearOptionLabel: 'All', dropdownPlaceholder: 'All',
    },
  ];

  const chipsSettings = {
    keyword: {
      prefix: 'keyword',
      type: 'text',
    },
    dateFrom: {
      prefix: 'after',
      type: 'date',
    },
    dateTo: {
      prefix: 'before',
      type: 'date',
    },
    purpose: {
      prefix: 'purpose',
      type: 'text',
    },
    workersPresent: {
      prefix: 'workers present',
      type: 'number',
    },
    workersReached: {
      prefix: 'workers reached',
      type: 'number',
    },
    completed: {
      prefix: 'visit status',
      type: 'dropdown',
    },
  };

  const viewModeButtons = [
    { mode: 'table', label: 'Table View', Icon: MdFormatListBulleted },
    { mode: 'gallery', label: 'Card View', Icon: AppsRounded },
  ];

  const handleSearch = (values) => {
    // Updates search parameters based on user input
    const updatedParams = {
      ...params,
      keyword: values.keyword || '', // Search by Keyword
      from: values.dateFrom || '', // Filter by Date from
      to: values.dateTo || '', // Filter by Date to
      purpose: values.purpose || '', // Filter by purpose
      total_workers: values.workersPresent || '', // Filter by number of workers present
      workers_interacted: values.workersReached || '', // Filter by number of workers reached
      completed: transformCompletedStatus(values.completed), // Transform 'Completed'/'Attempted' to true/false, 'All' returns undefined
      page: 1,
      perPage: itemsPerPage,
    };
    setCurrentPage(1);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page: 1,
      },
    });
  };

  const onPageChange = (page) => {
    const updatedParams = { ...params, page, perPage: itemsPerPage };
    setCurrentPage(page);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page,
      },
    });
  };

  // Change the URL when the view type is changed
  // Reset the currentPage to 1 whenever the view mode changes. This will prevent the pagination issue that arises when switching between views with different numbers of items per page.
  const handleModeChange = (mode) => {
    const updatedParams = {
      ...params,
      offset: 0,
    };

    setViewType(mode);
    setCurrentPage(1); // Reset to the first page when switching views
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page: 1,
        mode,
      },
    });
  };

  // Handle sorting by updating params
  const handleSort = (field) => {
    const newDirection = params.sortBy === field && params.direction === 'asc' ? 'desc' : 'asc';

    const updatedParams = {
      ...params,
      sortBy: field,
      direction: newDirection,
      page: 1,
      perPage: itemsPerPage,
    };

    setCurrentPage(1);
    setParams(updatedParams);

    updateSearchParamsAndUrl({
      params: {
        ...updatedParams,
        page: 1,
      },
    });
  };

  const renderView = () => {
    return (viewType === 'table') ? (
      <VisitsTable visits={visits} onSort={handleSort} isLoading={isLoading} />
    ) : (
      <VisitCards visits={visits} isLoading={isLoading} />
    );
  };

  const renderSkeletonLoader = () => {
    if (viewType === 'table') {
      return (
        <div className="flex justify-center w-full">
          <div className="pt-8 w-full">
            <TableSkeletonLoader numRows={itemsPerPage} numColumns={5} skeletonHeight={16} />
          </div>
        </div>
      );
    }
    const loaderKeys = Array.from({ length: itemsPerPage }, () => uuidv4());
    return (
      <div className="w-full">
        <div className="grid justify-start grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-6 gap-x-6 gap-y-8">
          {loaderKeys.map(key => (
            <VisitCardSkeletonLoader key={key} />
          ))}
        </div>
      </div>
    );
  };

  const renderViewModeButtons = () => {
    if (isLoading || visits.length === 0) return null;
    return (viewModeButtons.map(({ mode, label, Icon }) => (
      <ViewModeTooltip
        key={mode}
        id={`visits-${mode}-view-tooltip`}
        label={label}
      >
        <button
          type="button"
          onClick={() => handleModeChange(mode)}
          aria-label={label}
          className={`flex justify-center items-center w-8 h-8 rounded-md border-none bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'} focus:outline-none`}
        >
          <Icon
            size={24}
            color={`${viewType === mode ? tailwindTheme.theme.extend.colors['vamos-white'] : tailwindTheme.theme.extend.colors['vamos-black']}`}
            className={`flex items-center justify-center rounded-md bg-${viewType === mode ? 'vamos-green1' : 'vamos-white'}`}
          />
        </button>
      </ViewModeTooltip>
    )));
  };

  const renderContent = () => {
    if (isLoading) {
      return renderSkeletonLoader();
    }
    if (visits.length > 0) {
      return (
        <div>
          {renderView()}
          <footer>
            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={totalVisits}
              currentPage={currentPage}
              setCurrentPage={onPageChange}
              marginTop="my-9"
            />
          </footer>
        </div>
      );
    }

    if (totalUnfilteredVisits === 0) {
      return <EmptyState pageType="visits" onAdd={() => {}} />;
    }
    return <NoSearchResults pageType="visits" />;
  };

  return (
    <div className="flex flex-col">
      <div className="ml-2.75 w-full min-h-screen">
        <header>
          <div className="flex justify-between items-start w-full">
            {/* Breadcrumbs */}
            <Breadcrumbs links={breadcrumbLinks} />
            {/* Search Section */}
            <div className="self-start">
              <Search
                title="search visits"
                onSearch={handleSearch}
                fields={searchFields}
                chipsSettings={chipsSettings}
                activeSearchParams={mappedActiveSearchParams}
              />
            </div>
          </div>
        </header>
        <div className="flex justify-right w-full">
          <div className="flex w-full gap-4 justify-between items-center pb-4">
            <PagesHeader name="visits" label="Visits" size="h-9" dataTestId="visits-page-header" />
            {!isLoading && totalVisits > 0 && (
              <div className="flex-1 flex justify-center">
                <ResultCount
                  offset={currentOffset}
                  pageSize={visits.length}
                  total={totalVisits}
                  unfilteredTotal={totalUnfilteredVisits}
                  viewType={viewType}
                  pageType="visits"
                />
              </div>
            )}
            <div className="flex gap-2 items-center">
              {renderViewModeButtons()}
            </div>
          </div>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

VisitsPage.propTypes = {
  organizationId: PropTypes.number.isRequired,
};

export default withContextProvider(ToastProvider)(VisitsPage);
